// ============================================================================
// RequirePro — Gate component for Pro-only features
// Renders children for paid users; upgrade prompt for free/anonymous users.
// ============================================================================

import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, List, ListItem,
  ListItemIcon, ListItemText, Divider, CircularProgress, alpha, useTheme, Alert,
} from '@mui/material';
import {
  WorkspacePremium,
  CheckCircle,
  LockOutlined,
  Casino,
  TrendingUp,
  SwapHoriz,
  Whatshot,
  AllInclusive,
  NotificationsActive,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { AuthModal } from '../AuthModal';

const PRO_FEATURES = [
  { icon: <Casino fontSize="small" />, label: 'Fantasy / DFS Optimizer' },
  { icon: <TrendingUp fontSize="small" />, label: 'Explorer & Advanced Analytics' },
  { icon: <SwapHoriz fontSize="small" />, label: 'Trade Value Engine (CVR, DTV)' },
  { icon: <Whatshot fontSize="small" />, label: 'Hot / Cold Streak Tracker' },
  { icon: <AllInclusive fontSize="small" />, label: 'Unlimited AI Queries' },
  { icon: <NotificationsActive fontSize="small" />, label: 'CVR Mover Email Alerts' },
];

const RequirePro = ({ children, featureName }) => {
  const theme = useTheme();
  const { isPaid, loading, user, startCheckout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isPaid) return children;

  const handleUpgrade = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      await startCheckout();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Checkout unavailable. Please try again.';
      setCheckoutError(msg === 'Payments not configured' ? 'Payments are being set up — check back soon!' : msg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: 400,
          p: { xs: 2, sm: 4 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ width: '100%', maxWidth: 480 }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Lock icon */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 64, height: 64, borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <LockOutlined sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                </Box>
              </Box>

              {/* Headline */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip
                  icon={<WorkspacePremium fontSize="small" />}
                  label="Pro Feature"
                  size="small"
                  sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    background: `linear-gradient(135deg, #1a3a6e, #e8722a)`,
                    color: '#fff',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
                <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                  {featureName || 'Upgrade to Pro'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unlock every analytics tool for{' '}
                  <Typography component="span" fontWeight={700} color="primary.main">$8/month</Typography>.
                  Cancel anytime.
                </Typography>
              </Box>

              {/* Feature list */}
              <List dense disablePadding sx={{ mb: 3 }}>
                {PRO_FEATURES.map((f, i) => (
                  <React.Fragment key={f.label}>
                    <ListItem sx={{ py: 0.75, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36, color: theme.palette.primary.main }}>
                        {f.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={f.label}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      />
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    </ListItem>
                    {i < PRO_FEATURES.length - 1 && (
                      <Divider component="li" sx={{ opacity: 0.4 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>

              {/* CTA */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleUpgrade}
                disabled={checkoutLoading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #162f5a, #c96520)',
                  },
                }}
              >
                {checkoutLoading ? (
                  <CircularProgress size={22} sx={{ color: '#fff' }} />
                ) : user ? (
                  'Upgrade to Pro — $8/mo'
                ) : (
                  'Sign in to Upgrade'
                )}
              </Button>

              {checkoutError && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.82rem' }}>
                  {checkoutError}
                </Alert>
              )}

              {!user && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                  Already have an account?{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color="primary.main"
                    sx={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => setAuthOpen(true)}
                  >
                    Sign in
                  </Typography>
                </Typography>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={0} />
    </>
  );
};

export default RequirePro;
